import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import ReimaginedContributionHistory from 'tio-employee/components/reimagined/production/contribution-history';
import LoanStandingAlertCard from 'tio-employee/components/slr/loan-standing-alert-card';
import LoanStandingUploadDrawer from 'tio-employee/components/slr/loan-standing-upload-drawer';
import { shouldShowSlrLoanStandingAlert } from 'tio-employee/utils/slr-loan-standing-verification';
import type SlrLoanStandingVerificationService from 'tio-employee/services/slr-loan-standing-verification';
import type FeaturesService from 'tio-common/services/features';
import type { AuthenticatedSlrDashboardModel } from 'tio-employee/routes/authenticated/slr/dashboard';

interface SlrDashboardSignature {
  Args: {
    model: AuthenticatedSlrDashboardModel;
  };
}

export default class SlrDashboardRouteComponent extends Component<SlrDashboardSignature> {
  @service('slr-loan-standing-verification')
  declare slrLoanStandingVerification: SlrLoanStandingVerificationService;
  @service declare features: FeaturesService;

  // eslint-disable-next-line ember/no-tracked-properties-from-args -- intentional editable local copy: seeded from args then reassigned after upload (line 58)
  @tracked loanStandingVerification = this.args.model.loanStandingVerification;
  @tracked isLoanStandingUploadDrawerOpen = false;

  get reimaginedModel() {
    return this.args.model.reimagined;
  }

  // TODO: Also hide once the employee is already LRAP-enrolled (needs
  // server-side enrollment state). Flag-gated for now; the route guard
  // redirects here if the flag is off.
  get showLrapEnrollmentLink() {
    return this.features.isEnabled('lrapEnrollment');
  }

  get showLoanStandingAlert() {
    return shouldShowSlrLoanStandingAlert(this.loanStandingVerification);
  }

  openLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = true;
  };

  closeLoanStandingUploadDrawer = () => {
    this.isLoanStandingUploadDrawerOpen = false;
  };

  refreshLoanStandingVerification = dropTask(async () => {
    const personId = this.args.model.person.id;
    if (!personId) {
      throw new Error('Cannot refresh SLR loan standing without a person id.');
    }

    this.loanStandingVerification =
      await this.slrLoanStandingVerification.fetchCurrentState(personId);
  });

  <template>
    <div class="tio-col-tight">
      <h1 class="app-page-title px-4 pt-4">{{t "dashboard_tiles.student_loan_repayment"}}</h1>
      {{#if this.showLrapEnrollmentLink}}
        <div class="tio-lrap-enrollment-cta">
          <LinkTo
            @route="authenticated.slr.lrap.enrollment"
            class="tio-lrap-enrollment-cta__link"
            data-test-lrap-enrollment-link
          >
            <span class="tio-lrap-enrollment-cta__title">{{t "lrap.enrollment.cta_title"}}</span>
            <span class="tio-lrap-enrollment-cta__body">{{t "lrap.enrollment.cta_body"}}</span>
          </LinkTo>
        </div>
      {{/if}}
      {{#if this.loanStandingVerification}}
        {{#if this.showLoanStandingAlert}}
          <div class="px-4">
            <LoanStandingAlertCard
              @state={{this.loanStandingVerification}}
              @onOpenUpload={{this.openLoanStandingUploadDrawer}}
            />
          </div>
        {{/if}}
      {{/if}}
      <ReimaginedContributionHistory @model={{this.reimaginedModel}} />
    </div>

    {{#if this.loanStandingVerification}}
      <LoanStandingUploadDrawer
        @isOpen={{this.isLoanStandingUploadDrawerOpen}}
        @state={{this.loanStandingVerification}}
        @onClose={{this.closeLoanStandingUploadDrawer}}
        @onRefresh={{this.refreshLoanStandingVerification.perform}}
      />
    {{/if}}
  </template>
}
